import type { DomainSummary } from "@/features/data-hub/model/domainsSlice";

export type DomainColumn = {
  name: string;
  data_type: string;
  nullable: boolean;
  ordinal_position: number;
};

export type DomainForeignKey = {
  constrained_columns: string[];
  referred_table: string;
  referred_columns: string[];
};

export type DomainTable = {
  name: string;
  type: string;
  columns: DomainColumn[];
  row_count?: number | null;
  primary_key?: string[];
  foreign_keys?: DomainForeignKey[];
};

export type DomainSchema = {
  name: string;
  /**
   * Database this schema lives in. A connection exposes every database its role can
   * browse, so the schema name alone is not unique — two databases routinely both have
   * a `PUBLIC`. Absent on connection types with a single database (MySQL, Cube) and on
   * payloads written before databases were carried through; callers fall back to
   * {@link DomainMetadata.database} in that case.
   */
  database?: string;
  tables: DomainTable[];
};

export function isPrimaryKeyColumn(
  table: Pick<DomainTable, "primary_key">,
  columnName: string,
): boolean {
  return table.primary_key?.includes(columnName) ?? false;
}

export function isForeignKeyColumn(
  table: Pick<DomainTable, "foreign_keys">,
  columnName: string,
): boolean {
  return (
    table.foreign_keys?.some((foreignKey) =>
      foreignKey.constrained_columns.includes(columnName),
    ) ?? false
  );
}

export type DomainMetadata = {
  /** The connection's configured database — the session default, and the fallback
   * for schemas that do not name one. */
  database: string;
  /** Every database the catalog was read from, in display order. */
  databases?: string[];
  schemas: DomainSchema[];
};

export const SEMANTIC_GENERATION_STATUS = {
  PENDING: "pending",
  RUNNING: "running",
  /**
   * The model is finished and usable. Written by every path that produces one: a generation
   * run that succeeded, an imported model, and a publish with generation deliberately skipped
   * (hand-authored). One value on purpose — nothing downstream distinguishes how a usable
   * model was authored, so a per-path status would just be unreadable to all of it.
   */
  SUCCEEDED: "succeeded",
  FAILED: "failed",
  /** A generation run started and found nothing it could model. A dead end. */
  SKIPPED: "skipped",
} as const;

export type SemanticGenerationStatus =
  (typeof SEMANTIC_GENERATION_STATUS)[keyof typeof SEMANTIC_GENERATION_STATUS];

export const SEMANTIC_GENERATION_STAGE = {
  QUEUED: "queued",
  FETCHING_METADATA: "fetching_metadata",
  SAMPLING_TABLES: "sampling_tables",
  DISCOVERING_SEMANTICS: "discovering_semantics",
  PERSISTING_AND_EMBEDDING: "persisting_and_embedding",
} as const;

export type SemanticGenerationStage =
  (typeof SEMANTIC_GENERATION_STAGE)[keyof typeof SEMANTIC_GENERATION_STAGE];

export const SEMANTIC_GENERATION_STAGES_IN_ORDER: readonly SemanticGenerationStage[] =
  [
    SEMANTIC_GENERATION_STAGE.QUEUED,
    SEMANTIC_GENERATION_STAGE.FETCHING_METADATA,
    SEMANTIC_GENERATION_STAGE.SAMPLING_TABLES,
    SEMANTIC_GENERATION_STAGE.DISCOVERING_SEMANTICS,
    SEMANTIC_GENERATION_STAGE.PERSISTING_AND_EMBEDDING,
  ];

const SEMANTIC_GENERATION_STAGE_VALUES: ReadonlySet<string> = new Set(
  Object.values(SEMANTIC_GENERATION_STAGE),
);

export function isSemanticGenerationStage(
  value: unknown,
): value is SemanticGenerationStage {
  return typeof value === "string" && SEMANTIC_GENERATION_STAGE_VALUES.has(value);
}

export function resolveSemanticGenerationStage(
  status: SemanticGenerationStatus,
  stage: SemanticGenerationStage | null,
): SemanticGenerationStage {
  if (stage !== null) {
    return stage;
  }
  if (status === SEMANTIC_GENERATION_STATUS.PENDING) {
    return SEMANTIC_GENERATION_STAGE.QUEUED;
  }
  return SEMANTIC_GENERATION_STAGE.FETCHING_METADATA;
}

const SEMANTIC_GENERATION_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(SEMANTIC_GENERATION_STATUS),
);

function isSemanticGenerationStatus(
  value: unknown,
): value is SemanticGenerationStatus {
  return typeof value === "string" && SEMANTIC_GENERATION_STATUS_VALUES.has(value);
}

export function isSemanticGenerationInProgress(
  status: SemanticGenerationStatus,
): boolean {
  return (
    status === SEMANTIC_GENERATION_STATUS.PENDING ||
    status === SEMANTIC_GENERATION_STATUS.RUNNING
  );
}

/** Whether the domain detail page should poll GET /domains/:id for generation updates. */
export function shouldPollSemanticGeneration(
  status: SemanticGenerationStatus,
  pollAfterEnqueue: boolean,
): boolean {
  if (status === SEMANTIC_GENERATION_STATUS.RUNNING) {
    return true;
  }
  return (
    pollAfterEnqueue && status === SEMANTIC_GENERATION_STATUS.PENDING
  );
}

/**
 * Sections accepted by the unified `PATCH /domains/:id/semantic-model` endpoint —
 * mirrors the backend's SemanticModelSection literal (schemas/domain.py).
 */
export const SEMANTIC_MODEL_SECTION = {
  ENTITIES: "entities",
  MEASURES: "measures",
  METRICS: "metrics",
  RELATIONSHIPS: "relationships",
  GLOSSARY: "glossary",
  ONTOLOGIES: "ontologies",
  RULES: "rules",
  DIMENSIONS: "dimensions",
  CANDIDATES: "candidates",
} as const;

export type SemanticModelSection =
  (typeof SEMANTIC_MODEL_SECTION)[keyof typeof SEMANTIC_MODEL_SECTION];

/**
 * Per-type confidence thresholds sourced from the backend's single source of truth
 * (app.semantic_knowledge.services.semantic_builder.generation_caveats), returned on
 * every domain response. Consume via semanticConfidence.ts instead of hardcoding
 * values here — the two used to drift independently.
 */
export type ConfidenceThresholds = Record<string, number>;

/** Domain fields returned by ``GET /domains/:id`` (no connection catalog). */
export type DomainRecord = DomainSummary & {
  published: boolean;
  /** Whether chat may search this domain's uploaded documents. Off by default. */
  chat_documents_enabled: boolean;
  entities: Record<string, unknown>;
  draft_entities: Record<string, unknown>;
  generated_model: Record<string, unknown>;
  generation_status: SemanticGenerationStatus;
  generation_stage: SemanticGenerationStage | null;
  generation_progress_pct: number | null;
  generation_error: string | null;
  /** Markdown steering the next generation run. Absent on payloads written before it existed. */
  generation_instructions?: string | null;
  confidence_thresholds: ConfidenceThresholds;
};

/** Full domain payload from mutation endpoints that still bundle ``metadata``. */
export type DomainDetail = DomainRecord & {
  metadata: DomainMetadata;
};

function isDomainColumn(value: unknown): value is DomainColumn {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.name === "string" &&
    typeof row.data_type === "string" &&
    typeof row.nullable === "boolean" &&
    typeof row.ordinal_position === "number"
  );
}

function isDomainForeignKey(value: unknown): value is DomainForeignKey {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    Array.isArray(row.constrained_columns) &&
    row.constrained_columns.every((column) => typeof column === "string") &&
    typeof row.referred_table === "string" &&
    Array.isArray(row.referred_columns) &&
    row.referred_columns.every((column) => typeof column === "string")
  );
}

function isDomainTable(value: unknown): value is DomainTable {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.name === "string" &&
    typeof row.type === "string" &&
    Array.isArray(row.columns) &&
    row.columns.every(isDomainColumn) &&
    (row.row_count === undefined ||
      row.row_count === null ||
      typeof row.row_count === "number") &&
    (row.primary_key === undefined ||
      (Array.isArray(row.primary_key) &&
        row.primary_key.every((column) => typeof column === "string"))) &&
    (row.foreign_keys === undefined ||
      (Array.isArray(row.foreign_keys) && row.foreign_keys.every(isDomainForeignKey)))
  );
}

function isDomainSchema(value: unknown): value is DomainSchema {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.name === "string" &&
    (row.database === undefined || typeof row.database === "string") &&
    Array.isArray(row.tables) &&
    row.tables.every(isDomainTable)
  );
}

export function isDomainMetadata(value: unknown): value is DomainMetadata {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.database === "string" &&
    (row.databases === undefined ||
      (Array.isArray(row.databases) &&
        row.databases.every((database) => typeof database === "string"))) &&
    Array.isArray(row.schemas) &&
    row.schemas.every(isDomainSchema)
  );
}

function isConfidenceThresholds(value: unknown): value is ConfidenceThresholds {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return Object.values(value).every((threshold) => typeof threshold === "number");
}

function isDomainRecordFields(row: Record<string, unknown>): boolean {
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (typeof row.description === "string" || row.description === null) &&
    typeof row.connection_id === "number" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string" &&
    typeof row.published === "boolean" &&
    typeof row.chat_documents_enabled === "boolean" &&
    typeof row.entities === "object" &&
    row.entities !== null &&
    typeof row.draft_entities === "object" &&
    row.draft_entities !== null &&
    typeof row.generated_model === "object" &&
    row.generated_model !== null &&
    isSemanticGenerationStatus(row.generation_status) &&
    (row.generation_stage === null ||
      isSemanticGenerationStage(row.generation_stage)) &&
    (typeof row.generation_progress_pct === "number" ||
      row.generation_progress_pct === null) &&
    (typeof row.generation_error === "string" || row.generation_error === null) &&
    isConfidenceThresholds(row.confidence_thresholds)
  );
}

export function isDomainRecord(value: unknown): value is DomainRecord {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  return isDomainRecordFields(value as Record<string, unknown>);
}

export function isDomainDetail(value: unknown): value is DomainDetail {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return isDomainRecordFields(row) && isDomainMetadata(row.metadata);
}

export function isDomainMetadataResponse(
  value: unknown,
): value is { metadata: DomainMetadata } {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return isDomainMetadata(row.metadata);
}

/** Dataset-specific business context — ``GET/PATCH /domains/:id/business-context``. */
export type DomainBusinessContext = {
  domain_purpose: string | null;
  stakeholders: string[];
};

export function isDomainBusinessContext(
  value: unknown,
): value is DomainBusinessContext {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (typeof row.domain_purpose === "string" || row.domain_purpose === null) &&
    Array.isArray(row.stakeholders) &&
    row.stakeholders.every((stakeholder) => typeof stakeholder === "string")
  );
}
